import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bug,
  Sparkle,
  CaretDown,
  CaretUp,
  RocketLaunch,
} from "@phosphor-icons/react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { BugReport, BugStatus, ItemType } from "shared/types/bug-report";
import { useUsers } from "shared/contexts/users.context";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { format } from "date-fns";
import { PriorityStars } from "./PriorityStars";
import { bugPhaseColumns } from "../bugReportPhaseConfig";
import { UniversalPickerModal } from "@/shared/components/UniversalPickerModal";
import { PREDEFINED_BUG_REPORT_TAGS } from "../bugReportTags";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface BugReportListViewProps {
  bugs: BugReport[];
  onBugClick: (bug: BugReport) => void;
  onUpdate?: () => void;
}

type SortKey =
  | "type"
  | "id"
  | "createdAt"
  | "title"
  | "assignee"
  | "reporter"
  | "tags"
  | "priority"
  | "status";

type SortDirection = "asc" | "desc";

const PRIORITY_RANK: Record<string, number> = {
  Kritiek: 4,
  Hoog: 3,
  Normaal: 2,
  Laag: 1,
};

interface ColumnDef {
  key: SortKey;
  label: string;
  defaultWidth: number;
  minWidth: number;
  align?: "left" | "right" | "center";
}

const COLUMNS: ColumnDef[] = [
  { key: "type", label: "Type", defaultWidth: 70, minWidth: 50, align: "center" },
  { key: "id", label: "Nummer", defaultWidth: 110, minWidth: 70 },
  { key: "createdAt", label: "Aangemaakt", defaultWidth: 150, minWidth: 90 },
  { key: "title", label: "Titel", defaultWidth: 320, minWidth: 120 },
  { key: "tags", label: "Tags", defaultWidth: 200, minWidth: 80 },
  { key: "reporter", label: "Gemeld door", defaultWidth: 180, minWidth: 100 },
  { key: "assignee", label: "Toegewezen aan", defaultWidth: 180, minWidth: 100 },
  { key: "priority", label: "Prioriteit", defaultWidth: 100, minWidth: 70, align: "center" },
  { key: "status", label: "Status", defaultWidth: 140, minWidth: 80, align: "center" },
];

const COL_WIDTHS_STORAGE_KEY = "bugReportsListColWidths.v1";

// Deterministic, soft avatar color from a user id
const AVATAR_COLORS = [
  "bg-rose-200 text-rose-800",
  "bg-amber-200 text-amber-800",
  "bg-emerald-200 text-emerald-800",
  "bg-sky-200 text-sky-800",
  "bg-indigo-200 text-indigo-800",
  "bg-fuchsia-200 text-fuchsia-800",
  "bg-teal-200 text-teal-800",
  "bg-orange-200 text-orange-800",
];

const colorForId = (id?: string) => {
  if (!id) return "bg-neutral-200 text-neutral-700";
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const initials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

interface UserChipProps {
  userId?: string;
  name: string;
  muted?: boolean;
}

const UserChip: React.FC<UserChipProps> = ({ userId, name, muted }) => {
  if (!name || name === "—") {
    return (
      <span className="text-neutral-secondary-mid text-xs italic">—</span>
    );
  }
  return (
    <div className="flex items-center gap-1.5 truncate">
      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold ${colorForId(userId)}`}
        aria-hidden="true"
      >
        {initials(name)}
      </span>
      <span
        className={`truncate text-[13px] ${muted ? "text-neutral-secondary-mid" : "text-neutral-800"}`}
      >
        {name}
      </span>
    </div>
  );
};

export const BugReportListView: React.FC<BugReportListViewProps> = ({
  bugs,
  onBugClick,
  onUpdate,
}) => {
  const { users } = useUsers();
  const { session } = useSession();
  const [sortKey, setSortKey] = useState<SortKey>("createdAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [statusEditBug, setStatusEditBug] = useState<BugReport | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [tagsEditBug, setTagsEditBug] = useState<BugReport | null>(null);
  const [isUpdatingTags, setIsUpdatingTags] = useState(false);

  const tagPickerOptions = useMemo(() => {
    const known = new Set(PREDEFINED_BUG_REPORT_TAGS);
    bugs.forEach((bug) => bug.tags?.forEach((t) => known.add(t)));
    return Array.from(known).sort((a, b) => a.localeCompare(b, "nl"));
  }, [bugs]);

  const handleTagsChange = useCallback(
    async (newTags: string[]) => {
      if (!tagsEditBug) return;
      const sameTags =
        Array.isArray(tagsEditBug.tags) &&
        tagsEditBug.tags.length === newTags.length &&
        tagsEditBug.tags.every((t, i) => t === newTags[i]);
      if (sameTags) {
        setTagsEditBug(null);
        return;
      }

      setIsUpdatingTags(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/bug-reports/${tagsEditBug.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tags: newTags }),
          },
          session,
        );

        if (!response.ok) {
          throw new Error(
            `Failed to update bug tags (HTTP ${response.status})`,
          );
        }

        toast.success("Tags bijgewerkt");
        onUpdate?.();
      } catch (error) {
        console.error("Error updating bug tags:", error);
        toast.error("Fout bij bijwerken tags");
        throw error;
      } finally {
        setIsUpdatingTags(false);
        setTagsEditBug(null);
      }
    },
    [tagsEditBug, session, onUpdate],
  );

  const handleStatusChange = useCallback(
    async (newStatus: BugStatus) => {
      if (!statusEditBug) return;
      if (newStatus === statusEditBug.status) {
        setStatusEditBug(null);
        return;
      }

      setIsUpdatingStatus(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/bug-reports/${statusEditBug.id}/status`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: newStatus }),
          },
          session,
        );

        if (!response.ok) {
          throw new Error(
            `Failed to update bug status (HTTP ${response.status})`,
          );
        }

        toast.success(`Status bijgewerkt naar "${newStatus}"`);
        onUpdate?.();
      } catch (error) {
        console.error("Error updating bug status:", error);
        toast.error("Fout bij bijwerken status");
        throw error;
      } finally {
        setIsUpdatingStatus(false);
        setStatusEditBug(null);
      }
    },
    [statusEditBug, session, onUpdate],
  );

  // Column widths state (persisted)
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    if (typeof window === "undefined") {
      return Object.fromEntries(COLUMNS.map((c) => [c.key, c.defaultWidth]));
    }
    try {
      const stored = localStorage.getItem(COL_WIDTHS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, number>;
        return Object.fromEntries(
          COLUMNS.map((c) => [c.key, parsed[c.key] ?? c.defaultWidth]),
        );
      }
    } catch {
      // ignore parse errors
    }
    return Object.fromEntries(COLUMNS.map((c) => [c.key, c.defaultWidth]));
  });

  // Persist widths
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(colWidths));
    } catch {
      // ignore storage errors (quota, private mode, etc.)
    }
  }, [colWidths]);

  const totalWidth = useMemo(
    () => COLUMNS.reduce((sum, c) => sum + (colWidths[c.key] ?? c.defaultWidth), 0),
    [colWidths],
  );

  // Resize handling
  const resizingRef = useRef<{
    key: SortKey;
    startX: number;
    startWidth: number;
    minWidth: number;
  } | null>(null);

  const onResizeStart = useCallback(
    (e: React.MouseEvent, col: ColumnDef) => {
      e.preventDefault();
      e.stopPropagation();
      resizingRef.current = {
        key: col.key,
        startX: e.clientX,
        startWidth: colWidths[col.key] ?? col.defaultWidth,
        minWidth: col.minWidth,
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const onMove = (ev: MouseEvent) => {
        const r = resizingRef.current;
        if (!r) return;
        const dx = ev.clientX - r.startX;
        const next = Math.max(r.minWidth, r.startWidth + dx);
        setColWidths((prev) => ({ ...prev, [r.key]: next }));
      };

      const onUp = () => {
        resizingRef.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [colWidths],
  );

  const getUserName = (userId?: string) => {
    if (!userId) return "—";
    const u = users.find((x) => x.id === userId);
    return u ? `${u.first_name} ${u.last_name}`.trim() : "Onbekend";
  };

  const getPhaseConfig = (status: string) =>
    bugPhaseColumns.find((p) => p.id === status);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const sortedBugs = useMemo(() => {
    const copy = [...bugs];
    copy.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortKey) {
        case "type":
          av = a.type ?? "";
          bv = b.type ?? "";
          break;
        case "id":
          av = a.id;
          bv = b.id;
          break;
        case "createdAt":
          av = new Date(a.createdAt).getTime();
          bv = new Date(b.createdAt).getTime();
          break;
        case "title":
          av = a.title?.toLowerCase() ?? "";
          bv = b.title?.toLowerCase() ?? "";
          break;
        case "assignee":
          av = getUserName(a.assignedTo).toLowerCase();
          bv = getUserName(b.assignedTo).toLowerCase();
          break;
        case "reporter":
          av = getUserName(a.reportedBy).toLowerCase();
          bv = getUserName(b.reportedBy).toLowerCase();
          break;
        case "tags":
          av = (a.tags?.[0] ?? "").toLowerCase();
          bv = (b.tags?.[0] ?? "").toLowerCase();
          break;
        case "priority":
          av = PRIORITY_RANK[a.priority] ?? 0;
          bv = PRIORITY_RANK[b.priority] ?? 0;
          break;
        case "status":
          av = a.status ?? "";
          bv = b.status ?? "";
          break;
      }
      if (av < bv) return sortDirection === "asc" ? -1 : 1;
      if (av > bv) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [bugs, sortKey, sortDirection, users]);

  const renderStatusPicker = () => (
    <UniversalPickerModal
      isOpen={!!statusEditBug}
      onClose={() => {
        if (!isUpdatingStatus) setStatusEditBug(null);
      }}
      mode="bug-status"
      title={
        statusEditBug
          ? `Wijzig status voor ${statusEditBug.type === ItemType.BUG ? "BUG" : "FEAT"}-${statusEditBug.id}`
          : "Wijzig status"
      }
      onConfirm={(status) => {
        void handleStatusChange(status as BugStatus);
      }}
      config={{
        defaultValue: statusEditBug?.status,
      }}
    />
  );

  const renderTagsPicker = () => (
    <UniversalPickerModal
      isOpen={!!tagsEditBug}
      onClose={() => {
        if (!isUpdatingTags) setTagsEditBug(null);
      }}
      mode="custom-tags"
      title={
        tagsEditBug
          ? `Wijzig tags voor ${tagsEditBug.type === ItemType.BUG ? "BUG" : "FEAT"}-${tagsEditBug.id}`
          : "Wijzig tags"
      }
      onConfirm={(value) => {
        void handleTagsChange(Array.isArray(value) ? (value as string[]) : []);
      }}
      config={{
        multiSelect: true,
        defaultValue: tagsEditBug?.tags ?? [],
        customOptions: tagPickerOptions,
      }}
    />
  );

  if (bugs.length === 0) {
    return (
      <>
        <div className="rounded-md border border-neutral-200 bg-white p-12 text-center">
          <p className="text-neutral-secondary-mid text-sm">
            Geen items gevonden
          </p>
        </div>
        {renderStatusPicker()}
        {renderTagsPicker()}
      </>
    );
  }

  const alignClass = (a?: "left" | "right" | "center") =>
    a === "right"
      ? "justify-end text-right"
      : a === "center"
        ? "justify-center text-center"
        : "justify-start text-left";

  return (
    <>
    <div className="w-full overflow-x-auto rounded-md border border-neutral-200 bg-white">
      <table
        className="border-collapse text-[13px]"
        style={{ tableLayout: "fixed", width: `${totalWidth}px` }}
      >
        <colgroup>
          {COLUMNS.map((c) => (
            <col
              key={c.key}
              style={{ width: `${colWidths[c.key] ?? c.defaultWidth}px` }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map((c) => {
              const isActive = sortKey === c.key;
              return (
                <th
                  key={c.key}
                  scope="col"
                  onClick={() => handleSort(c.key)}
                  className="group relative cursor-pointer select-none border-b border-neutral-200 bg-neutral-50 px-3 py-2 text-[12px] font-medium text-neutral-700"
                >
                  <div className={`flex items-center gap-1 ${alignClass(c.align)}`}>
                    <span className="truncate">{c.label}</span>
                    <span
                      className={`shrink-0 transition-opacity ${
                        isActive
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-50"
                      }`}
                      aria-hidden="true"
                    >
                      {isActive && sortDirection === "asc" ? (
                        <CaretUp size={11} weight="bold" />
                      ) : (
                        <CaretDown size={11} weight="bold" />
                      )}
                    </span>
                  </div>
                  {/* Resize handle on the right edge */}
                  <span
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Pas kolombreedte ${c.label} aan`}
                    onMouseDown={(e) => onResizeStart(e, c)}
                    onClick={(e) => e.stopPropagation()}
                    className="absolute right-0 top-0 bottom-0 z-10 w-[6px] -mr-px cursor-col-resize select-none after:absolute after:right-0 after:top-1 after:bottom-1 after:w-px after:bg-neutral-300 after:opacity-0 hover:after:opacity-100"
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedBugs.map((bug, idx) => {
            const safeType =
              bug.type === ItemType.BUG || bug.type === ItemType.FEATURE
                ? bug.type
                : ItemType.BUG;
            const isBug = safeType === ItemType.BUG;
            const TypeIcon = isBug ? Bug : Sparkle;
            const typeColor = isBug ? "text-brick-primary" : "text-blue-600";
            const prefix = isBug ? "BUG" : "FEAT";
            const phase = getPhaseConfig(bug.status);
            const reporterName = getUserName(bug.reportedBy);
            const assigneeName = getUserName(bug.assignedTo);
            const dateStr = format(new Date(bug.createdAt), "dd-MM-yyyy HH:mm");

            return (
              <tr
                key={bug.id}
                onClick={() => onBugClick(bug)}
                className={`group cursor-pointer transition-colors hover:bg-moss-primary/5 ${
                  idx % 2 === 1 ? "bg-neutral-50/40" : "bg-white"
                }`}
              >
                <td className="border-b border-neutral-100 px-3 py-2 text-center align-middle">
                  <TypeIcon
                    weight="duotone"
                    size={18}
                    className={`inline-block ${typeColor}`}
                  />
                </td>
                <td className="border-b border-neutral-100 px-3 py-2 align-middle">
                  <span className="font-mono text-[12px] font-semibold text-neutral-800">
                    {prefix}-{bug.id}
                  </span>
                </td>
                <td className="text-neutral-secondary-mid border-b border-neutral-100 px-3 py-2 align-middle text-[12px] tabular-nums">
                  {dateStr}
                </td>
                <td className="border-b border-neutral-100 px-3 py-2 align-middle">
                  <div className="flex flex-col">
                    {bug.description?.trim() ? (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="group-hover:text-moss-primary truncate text-[13px] font-medium text-neutral-900 transition-colors">
                              {bug.title}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="bottom"
                            align="start"
                            className="max-w-md whitespace-pre-wrap break-words"
                          >
                            <p>{bug.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="group-hover:text-moss-primary truncate text-[13px] font-medium text-neutral-900 transition-colors">
                        {bug.title}
                      </span>
                    )}
                    {bug.epic && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/bug-reports/epics/${bug.epic!.id}`;
                        }}
                        className="text-moss-primary mt-0.5 inline-flex w-fit items-center gap-1 text-[11px] hover:underline"
                        title={`Ga naar epic: ${bug.epic.title}`}
                      >
                        <RocketLaunch size={10} weight="fill" />
                        <span className="truncate">{bug.epic.title}</span>
                      </button>
                    )}
                  </div>
                </td>
                <td className="border-b border-neutral-100 px-3 py-2 align-middle">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTagsEditBug(bug);
                    }}
                    title="Klik om tags te wijzigen"
                    className="hover:bg-moss-primary/5 focus-visible:ring-moss-primary/40 -mx-1 flex w-[calc(100%+0.5rem)] items-center rounded px-1 py-0.5 text-left transition-colors focus:outline-none focus-visible:ring-2"
                  >
                    {bug.tags && bug.tags.length > 0 ? (
                      <div className="flex flex-wrap gap-1 overflow-hidden">
                        {bug.tags.slice(0, 2).map((tag, i) => (
                          <span
                            key={i}
                            className="inline-flex max-w-[100px] truncate rounded-sm bg-neutral-100 px-1.5 py-px text-[11px] text-neutral-700"
                          >
                            {tag}
                          </span>
                        ))}
                        {bug.tags.length > 2 && (
                          <span className="inline-flex rounded-sm bg-neutral-100 px-1.5 py-px text-[11px] text-neutral-700">
                            +{bug.tags.length - 2}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-neutral-secondary-mid text-xs italic">
                        + tag
                      </span>
                    )}
                  </button>
                </td>
                <td className="border-b border-neutral-100 px-3 py-2 align-middle">
                  <UserChip userId={bug.reportedBy} name={reporterName} />
                </td>
                <td className="border-b border-neutral-100 px-3 py-2 align-middle">
                  <UserChip
                    userId={bug.assignedTo}
                    name={assigneeName}
                    muted={!bug.assignedTo}
                  />
                </td>
                <td className="border-b border-neutral-100 px-3 py-2 text-center align-middle">
                  <div className="inline-flex">
                    <PriorityStars priority={bug.priority} />
                  </div>
                </td>
                <td className="border-b border-neutral-100 px-3 py-2 text-center align-middle">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStatusEditBug(bug);
                    }}
                    title="Klik om status te wijzigen"
                    className="inline-flex items-center rounded-full transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-moss-primary/40"
                  >
                    {phase ? (
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${phase.color} ${phase.headerBgColor} ${phase.borderColor}`}
                      >
                        {phase.label}
                      </span>
                    ) : (
                      <span className="text-neutral-secondary-mid text-xs">
                        {bug.status}
                      </span>
                    )}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {renderStatusPicker()}
    {renderTagsPicker()}
    </>
  );
};
